import { environment } from './../../../environments/environment';
import {
    Component,
    OnInit,
    OnDestroy,
    ViewEncapsulation,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    Validators,
} from '@angular/forms';

import { Store } from '@ngrx/store';

import { Base64Service } from '../../core/base64.service';
import { LoggerService } from '../../core/logger.service';

import * as fromRoot from '../../reducers';
import * as CoreActions from '../../actions/core.actions';

import { AuthUserPassword, BlendEvent, ErrCode } from '../../shared/models';
import { Observable, Subscription } from 'rxjs';
import * as AuthActions from '../../actions/auth.actions';
import { DispatchService } from '../../core/dispatch.service';
import { ShareInviteService } from '../../shares/share-invite.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureService } from 'src/app/shared/services/feature.service';
import { JoblockService } from '../../core/joblock.service';
import { DomSanitizer } from '@angular/platform-browser';
import { BlendService } from 'src/app/shared/services/blend.service';
import { UserService } from 'src/app/core/user.service';
import { CookieService } from 'ngx-cookie-service';
import { SyncCryptService } from 'src/app/core/crypt/sync-crypt.service';
import { AuthService } from '../services/auth.service';

@Component({
    selector: 'sync-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.css'],
    encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
    public verified: boolean;
    public passwordset: boolean;
    public passwordreset: boolean;
    public spinner = false;
    public loginForm: FormGroup;
    public error: ErrCode;
    public loginProgress: number;
    public twofaRequired: boolean;
    public ctx: string;

    // When joining a share, these fields are used.
    private cachekey: string;
    private wrapKey: string;
    public shareLabel: string;
    public shareFolderName: string;
    private subscription: Subscription;
    public accordianContent = [];

    constructor(
        private Dispatch: DispatchService,
        private store: Store<fromRoot.State>,
        private fb: FormBuilder,
        private Base64: Base64Service,
        private log: LoggerService,
        private shareInviteService: ShareInviteService,
        private router: Router,
        private route: ActivatedRoute,
        private featureService: FeatureService,
        private JoblockService: JoblockService,
        private sanitized: DomSanitizer,
        private blendService: BlendService,
        private userService: UserService,
        private cookies: CookieService,
        private crypt: SyncCryptService,
    ) { }

    ngOnDestroy() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }
    ngOnInit() {
        this.accordianContent = [{
            title: 'I didn\'t receive my 2FA code in my email',
            paras: [
                'Codes are only sent by email to accounts with email-based 2FA enabled.',
                'If enabled, an email with your 2FA code should arrive in your inbox within 5 minutes. Please double check your junk or spam folders.',
                this.sanitized.bypassSecurityTrustHtml('If you haven\'t received an email after 5 minutes, then an incorrect username and password combination may be why you did not recieve a 2FA code in your email. Try <a href="/login">signing in</a> again, or <a href="/forgotpassword">resetting your password</a>.')
            ],
        }, {
            title: 'My authenticator app code is not working',
            paras: [
                'Codes from an authenticator app will only work for an account currently linked to the application.',
                this.sanitized.bypassSecurityTrustHtml('If your account is already linked, then an incorrect username and password combination may be why your 2FA code is not working. Try <a href="/login">signing in</a> again, or <a href="/forgotpassword">resetting your password</a>.')
            ],
        }];
        this.loginProgress = 0;
        this.store.dispatch(new CoreActions.StoreClearAction());
        this.store.dispatch(new AuthActions.LogoutAction());
        this.createForm();
        this.subscription = this.store
            .select(fromRoot.getAuthState)
            .subscribe((data) => {
                if (data && !data.authenticated && data.error) {
                    this.loginProgress = 0;
                    this.spinner = false;
                    this.log.e('Error log in ', data.error);
                    switch (data.error.code) {
                        case 8050:
                        case 8051:
                            // suspended
                            this.router.navigate(['/account-suspended'], { queryParams: data.error.data });
                            break;
                        case 8054:
                            // incomplete
                            this.router.navigate(['/account-suspended'], { queryParams: { ...data.error.data, m: 2 } });
                            break;
                        case 8055:
                            // migrating
                            this.router.navigate(['/account-suspended'], { queryParams: { ...data.error.data, m: 1 } });
                            break;
                        case 8052:
                            this.router.navigate(['/accounting-hold'], { queryParams: data.error.data });
                            break;
                        case 9429:
                            this.error = data.error;
                            this.loginProgress = 0;
                            this.spinner = false;
                            this.twofaRequired = false;
                            this.loginForm.get('twofacode').setValue('');
                            this.loginForm.get('password').setValue('');
                            break;
                        case 8022:
                            this.spinner = false;
                            this.twofaRequired = true;
                            this.loginProgress = 0;
                            this.loginForm.get('twofacode').setValue('');
                            break;
                        default:
                            this.error = new ErrCode(6001);
                            this.loginProgress = 0;
                            this.spinner = false;
                            this.twofaRequired = false;
                            this.loginForm.get('twofacode').setValue('');
                            this.loginForm.get('password').setValue('');
                            break;
                    }
                    // reset the auth state, update the validity of the form
                    // and then early exit the function
                    this.store.dispatch(new AuthActions.LogoutAction());
                    this.loginForm.updateValueAndValidity();
                    return;
                }

                if (data.twofarequired) {
                    this.spinner = false;
                    this.twofaRequired = true;
                    this.loginProgress = 0;
                }
                if (data.progress > this.loginProgress) {
                    this.loginProgress = data.progress;
                    // the current bCrypt lib we use stops at
                    // 85 for progress - set the progress to 100 instead.
                    if (data.authenticated || data.progress >= 85) {
                        this.loginProgress = 100;
                    }
                }

                if (data.authenticated && this.ctx != 'share') {
                    this.handleLogin();
                    this.spinner = false;
                } else if (data.authenticated && this.ctx == 'share') {
                    this.handleShareLogin();
                    this.spinner = false;
                }
            });
        // console.log(this.$stateParams);
        this.ctx = this.route.snapshot.queryParams['ctx'];
        this.verified = this.route.snapshot.queryParams['verified'] == 1;
        this.passwordset = this.route.snapshot.queryParams['passwordset'] == 1;
        this.passwordreset = this.route.snapshot.queryParams['passwordreset'] == 1;
        switch (this.route.snapshot.queryParams['ctx']) {
            case 'share':
                this.cachekey = this.route.snapshot.queryParams['cachekey'];
                this.wrapKey = this.route.snapshot.fragment;
                this.handleShare();
        }

        this.featureService.resetFeatureFlags();
    }

    public toggleAccordian(index: number) {
        document.getElementById('panel' + index).classList.toggle('in');
        document.getElementById('collapse' + index).classList.toggle('in');
    }

    public onSubmit() {
        // saved for debugging
        // Object.keys(this.loginForm.controls).forEach(key => {
        //     const controlErrors= this.loginForm.get(key).errors;
        //     if (controlErrors != null) {
        //         Object.keys(controlErrors).forEach(keyError => {
        //         console.log('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
        //         });
        //     }
        // });
        // console.log(this.loginForm.valid);
        // if (!this.loginForm.valid) { console.log('asldkfj'); return; }

        this.spinner = true;
        this.error = undefined;
        const item = new AuthUserPassword(
            this.loginForm.get('username').value,
            this.loginForm.get('password').value,
            this.loginForm.get('twofacode').value
        );

        this.store.dispatch(new AuthActions.LoginAction(item));
    }

    private async handleLogin() {
        const sel = this.store.select(fromRoot.getSignApiData);
        const sub = sel.subscribe(async (data) => {
            const values = {
                access_token: data.access_token,
                access_secret: await this.crypt.storeDecrypt(
                    'access_secret'
                ),
                uid: data.uid,
                web_device_id: data.web_device_id,
            };
            const token = this.Base64.encode(Object.values(values).join('|'));
            const expDate = new Date();
            expDate.setMonth(expDate.getMonth() + 3);

            this.cookies.set(
                'sync_auth',
                token,
                expDate,
                '/',
                environment.cookiedomain,
                environment.cookiesecure,
                'Lax'
            );
            sub.unsubscribe();
        });

        this.blendService.track(BlendEvent.SIGN_IN);
        if (this.route.snapshot.queryParams['loc']) {
            this.Dispatch.run();
        } else if (this.route.snapshot.queryParams['return_to']) {
            const path = decodeURIComponent(this.route.snapshot.queryParams['return_to']);
            window.location.replace(
                path.substring(0, 1) == '/' ? path : `/${path}`
            );
        } else if (this.route.snapshot.queryParams['return_to_link']) {
            const path = decodeURIComponent(this.route.snapshot.queryParams['return_to_link']);
            const linkPath = this.route.snapshot.fragment
                ? `${environment.linkhost}${path}#${this.route.snapshot.fragment}`
                : `${environment.linkhost}${path}`;
            window.location.replace(linkPath);
        } else {
            const dashboard = this.userService.get('dashboard') || 'files';
            this.router.navigate([`/${dashboard}`], { queryParams: undefined });
            return;
        }
    }

    public async handleShareLogin() {
        const inviteData = await this.shareInviteService.getInvite(
            this.cachekey,
            this.wrapKey
        );
        const shareAcceptJob = await this.shareInviteService.accept(this.wrapKey, 0, inviteData);
        this.router.navigate(['/shares'], { queryParams: undefined });
    }

    public async handleShare() {
        if (!this.route.snapshot.queryParams['cachekey']) {
            this.log.error('Missing cachekey, cannot continue');
            this.ctx = undefined;
            return;
        }
        if (!this.wrapKey) {
            this.log.error('Missing wrap key, cannot continue');
            this.ctx = undefined;
            return;
        }

        try {
            const data = await this.shareInviteService.getInvitePublic(
                this.route.snapshot.queryParams['cachekey'],
                this.wrapKey
            );
            this.shareFolderName = data.name;
            this.shareLabel = data.label;
        } catch (err) {
            if (typeof err == 'object' && err.code) {
                if (err.code == 1620) {
                    this.wrapKey = '';
                    this.ctx = undefined;
                }
                this.error = err;
            } else if (typeof err == 'object' && err.error_code == 1600) {
                this.ctx = undefined;
                this.error = ErrCode.fromApiLegacy(err);
            } else if (typeof err == 'object' && err.error_code) {
                this.error = ErrCode.fromApiLegacy(err);
            } else {
                this.error = new ErrCode(
                    1000,
                    'An error retrieving share data occurred'
                );
            }
        }
    }

    private requiredIfValidator(predicate: () => boolean) {
        return (formControl => {
            if (!formControl.parent) {
                return null;
            }
            if (predicate()) {
                return Validators.required(formControl);
            }
            return null;
        });
    }
    private createForm() {
        const username = this.route.snapshot.queryParams['username']
            ? this.Base64.decode(this.route.snapshot.queryParams['username'])
            : '';

        this.loginForm = this.fb.group({
            username: [username, [Validators.required, Validators.email]],
            password: ['', Validators.required],
            rememberme: true,
            twofacode: ['', this.requiredIfValidator(() => this.twofaRequired)],
        });
    }
}
